import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { sectionForPath } from "@/videoMap";
import { DEFAULT_TOUR, TOURS } from "@/tours";

export default function PageTour() {
  const { t, lang } = useI18n();
  const { pathname } = useLocation();
  const [idx, setIdx] = useState(-1);
  const [, setTick] = useState(0);

  const section = sectionForPath(pathname);
  const steps = (section && TOURS[section]) || DEFAULT_TOUR;

  useEffect(() => { setIdx(-1); }, [pathname]);
  useEffect(() => {
    const h = () => {
      setIdx(0);
      const s = steps[0];
      if (s?.sel) {
        const el = document.querySelector(s.sel);
        if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      setTimeout(() => setTick((x) => x + 1), 420);
    };
    window.addEventListener("cc-start-tour", h);
    return () => window.removeEventListener("cc-start-tour", h);
  }, [steps]);
  useEffect(() => {
    if (idx < 0) return undefined;
    const re = () => setTick((x) => x + 1);
    window.addEventListener("resize", re);
    window.addEventListener("scroll", re, true);
    return () => { window.removeEventListener("resize", re); window.removeEventListener("scroll", re, true); };
  }, [idx]);

  if (!pathname.startsWith("/app")) return null;

  const goto = (i) => {
    setIdx(i);
    const s = steps[i];
    if (s?.sel) {
      const el = document.querySelector(s.sel);
      if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
      setTimeout(() => setTick((x) => x + 1), 420);
    }
  };

  if (idx < 0) return null;

  const step = steps[idx];
  const txt = step[lang === "it" ? "it" : "en"] || step.en || step.it;
  let rect = null;
  if (step.sel) {
    const el = document.querySelector(step.sel);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) rect = r;
    }
  }
  const vw = window.innerWidth, vh = window.innerHeight;
  const cardW = Math.min(360, vw - 24);
  let cardStyle;
  if (rect) {
    const below = rect.bottom + 12 + 200 < vh;
    cardStyle = {
      position: "fixed", width: cardW, zIndex: 170,
      left: Math.min(Math.max(rect.left, 12), vw - cardW - 12),
      ...(below ? { top: rect.bottom + 12 } : { bottom: vh - rect.top + 12 }),
    };
  } else {
    cardStyle = { position: "fixed", width: cardW, zIndex: 170, left: (vw - cardW) / 2, top: Math.max(60, vh / 2 - 130) };
  }
  const last = idx === steps.length - 1;

  return (
    <div data-testid="page-tour-overlay">
      {rect ? (
        <div style={{
          position: "fixed", zIndex: 160, pointerEvents: "none",
          left: rect.left - 6, top: rect.top - 6, width: rect.width + 12, height: rect.height + 12,
          borderRadius: 14, border: "2px solid #C5A880",
          boxShadow: "0 0 0 9999px rgba(10,16,32,0.6)",
        }} data-testid="page-tour-highlight" />
      ) : (
        <div style={{ position: "fixed", inset: 0, zIndex: 160, background: "rgba(10,16,32,0.6)" }} onClick={() => setIdx(-1)} />
      )}
      <div style={cardStyle} className="bg-card border border-border rounded-2xl shadow-2xl p-4 fade-up" data-testid="page-tour-card">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="pill bg-accent/15 text-accent text-[10px]" data-testid="page-tour-counter">{idx + 1}/{steps.length}</div>
          <button onClick={() => setIdx(-1)} className="p-1 rounded-lg text-muted-foreground hover:text-foreground" data-testid="page-tour-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="font-bold text-sm mb-1" data-testid="page-tour-title">{txt.t}</div>
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">{txt.b}</p>
        <div className="flex items-center justify-between">
          <button onClick={() => goto(idx - 1)} disabled={idx === 0}
            className="btn-ghost text-xs border border-border rounded-lg disabled:opacity-30" data-testid="page-tour-prev">
            <ArrowLeft className="w-3.5 h-3.5" /> {t("tourPrevLbl")}
          </button>
          {last ? (
            <button onClick={() => setIdx(-1)} className="btn-primary text-xs" data-testid="page-tour-finish">✓ {t("tourDoneLbl")}</button>
          ) : (
            <button onClick={() => goto(idx + 1)} className="btn-primary text-xs" data-testid="page-tour-next">
              {t("tourNext")} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
